/**
 * News Service - Fetches developer and tech news
 * Uses Hacker News API (completely free) and NewsAPI for broader coverage
 */

const HACKER_NEWS_API_BASE = 'https://hacker-news.firebaseio.com/v0'
const CORS_PROXY = 'https://corsproxy.io/?'  // CORS proxy for client-side requests

export class NewsService {
  constructor() {
    this.cache = new Map()
    this.cacheExpiry = 10 * 60 * 1000 // 10 minutes in milliseconds
  }

  /**
   * Get cache key for news data
   */
  getCacheKey(type) {
    return `news-${type}`
  }

  /**
   * Check if cached data is still valid
   */
  isCacheValid(cacheEntry) {
    return cacheEntry && (Date.now() - cacheEntry.timestamp) < this.cacheExpiry
  }

  /**
   * Fetch data with CORS proxy
   */
  async fetchWithCORS(url) {
    const response = await fetch(`${CORS_PROXY}${encodeURIComponent(url)}`)
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    return response.json()
  }

  /**
   * Fetch top stories from Hacker News
   */
  async fetchHackerNewsTopStories(limit = 10) {
    try {
      console.log('📰 Fetching Hacker News top stories...')
      
      // Get list of top story IDs
      const topStoriesUrl = `${HACKER_NEWS_API_BASE}/topstories.json`
      const storyIds = await fetch(topStoriesUrl).then(res => res.json())
      
      // Get first 'limit' stories
      const stories = []
      for (let i = 0; i < Math.min(limit, storyIds.length); i++) {
        const storyId = storyIds[i]
        const storyUrl = `${HACKER_NEWS_API_BASE}/item/${storyId}.json`
        
        try {
          const story = await fetch(storyUrl).then(res => res.json())
          if (story && story.title && !story.deleted && !story.dead) {
            stories.push(this.formatHackerNewsStory(story))
          }
        } catch (error) {
          console.warn(`Failed to fetch story ${storyId}:`, error)
          continue
        }
      }
      
      console.log(`📰 Fetched ${stories.length} Hacker News stories`)
      return stories
    } catch (error) {
      console.error('Hacker News API error:', error)
      throw error
    }
  }

  /**
   * Format Hacker News story for consistent structure
   */
  formatHackerNewsStory(story) {
    const timeAgo = this.getTimeAgo(story.time * 1000) // Convert Unix timestamp to milliseconds
    
    return {
      id: story.id,
      title: story.title,
      url: story.url || `https://news.ycombinator.com/item?id=${story.id}`,
      source: 'Hacker News',
      author: story.by || 'Anonymous',
      publishedAt: new Date(story.time * 1000).toISOString(),
      timeAgo,
      score: story.score || 0,
      commentCount: story.descendants || 0,
      type: story.type || 'story',
      description: this.extractDescription(story),
      category: this.categorizeStory(story.title)
    }
  }

  /**
   * Extract description from story
   */
  extractDescription(story) {
    if (story.text) {
      // Remove HTML tags and truncate
      const text = story.text.replace(/<[^>]*>/g, '').trim()
      return text.length > 100 ? text.substring(0, 100) + '...' : text
    }
    
    // Fallback to generating description from title/URL
    if (story.url) {
      try {
        const domain = new URL(story.url).hostname.replace('www.', '')
        return `Article from ${domain}`
      } catch {
        return 'External article'
      }
    }
    
    return 'Discussion on Hacker News'
  }

  /**
   * Categorize story based on title keywords
   */
  categorizeStory(title) {
    const titleLower = title.toLowerCase()
    
    if (titleLower.includes('ai') || titleLower.includes('artificial intelligence') || 
        titleLower.includes('machine learning') || titleLower.includes('ml')) {
      return 'AI/ML'
    }
    
    if (titleLower.includes('react') || titleLower.includes('vue') || titleLower.includes('angular') ||
        titleLower.includes('javascript') || titleLower.includes('typescript') || titleLower.includes('frontend')) {
      return 'Frontend'
    }
    
    if (titleLower.includes('python') || titleLower.includes('java') || titleLower.includes('golang') ||
        titleLower.includes('rust') || titleLower.includes('programming') || titleLower.includes('code')) {
      return 'Programming'
    }
    
    if (titleLower.includes('startup') || titleLower.includes('funding') || titleLower.includes('vc') ||
        titleLower.includes('business') || titleLower.includes('company')) {
      return 'Business'
    }
    
    if (titleLower.includes('crypto') || titleLower.includes('bitcoin') || titleLower.includes('blockchain')) {
      return 'Crypto'
    }
    
    if (titleLower.includes('security') || titleLower.includes('privacy') || titleLower.includes('hack')) {
      return 'Security'
    }
    
    return 'Tech'
  }

  /**
   * Calculate time ago from timestamp
   */
  getTimeAgo(timestamp) {
    const now = Date.now()
    const diff = now - timestamp
    
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    
    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    if (minutes > 0) return `${minutes}m ago`
    return 'Just now'
  }

  /**
   * Get news with caching
   */
  async getNews(type = 'top', limit = 10) {
    const cacheKey = this.getCacheKey(`${type}-${limit}`)
    const cached = this.cache.get(cacheKey)

    // Return cached data if valid
    if (this.isCacheValid(cached)) {
      console.log(`📰 Using cached news data for ${type}`)
      return cached.data
    }

    try {
      let news = []
      
      switch (type) {
        case 'top':
        default:
          news = await this.fetchHackerNewsTopStories(limit)
          break
      }
      
      // Cache the result
      this.cache.set(cacheKey, {
        data: news,
        timestamp: Date.now()
      })

      return news
    } catch (error) {
      console.error('Error fetching news:', error)
      
      // If API fails and we have cached data, use it even if expired
      if (cached) {
        console.log(`📰 Using expired cache due to API error`)
        return cached.data
      }
      
      // Return demo data as fallback
      return this.getDemoNews()
    }
  }

  /**
   * Get news for the widget (simplified format)
   */
  async getWidgetNews() {
    try {
      const news = await this.getNews('top', 3)
      return news.map(article => ({
        title: article.title,
        description: article.category,
        source: article.source,
        timeAgo: article.timeAgo,
        score: article.score,
        url: article.url
      }))
    } catch (error) {
      console.error('Error fetching widget news:', error)
      return this.getDemoWidgetNews()
    }
  }

  /**
   * Demo news data for fallback
   */
  getDemoNews() {
    return [
      {
        id: 'demo-1',
        title: 'New React Features Revolutionize Frontend Development',
        url: '#',
        source: 'Hacker News',
        author: 'techdev',
        publishedAt: new Date().toISOString(),
        timeAgo: '2h ago',
        score: 142,
        commentCount: 45,
        type: 'story',
        description: 'Latest React updates bring exciting new capabilities...',
        category: 'Frontend'
      },
      {
        id: 'demo-2', 
        title: 'AI Breakthrough: New Model Achieves Human-Level Performance',
        url: '#',
        source: 'Hacker News',
        author: 'airesearcher',
        publishedAt: new Date().toISOString(),
        timeAgo: '4h ago',
        score: 286,
        commentCount: 93,
        type: 'story',
        description: 'Revolutionary AI model shows unprecedented results...',
        category: 'AI/ML'
      },
      {
        id: 'demo-3',
        title: 'Rust Language Adoption Grows 300% in Enterprise',
        url: '#',
        source: 'Hacker News',
        author: 'rustdev',
        publishedAt: new Date().toISOString(),
        timeAgo: '6h ago',
        score: 198,
        commentCount: 67,
        type: 'story',
        description: 'Major companies increasingly adopting Rust...',
        category: 'Programming'
      }
    ]
  }

  /**
   * Demo widget news for fallback
   */
  getDemoWidgetNews() {
    return [
      {
        title: 'New React Features Available',
        description: 'Frontend',
        source: 'Hacker News',
        timeAgo: '2h ago',
        score: 142,
        url: '#'
      },
      {
        title: 'AI Breakthrough Announced',
        description: 'AI/ML', 
        source: 'Hacker News',
        timeAgo: '4h ago',
        score: 286,
        url: '#'
      },
      {
        title: 'Rust Adoption Growing Fast',
        description: 'Programming',
        source: 'Hacker News', 
        timeAgo: '6h ago',
        score: 198,
        url: '#'
      }
    ]
  }

  /**
   * Clear cache (useful for development/debugging)
   */
  clearCache() {
    this.cache.clear()
    console.log('📰 News cache cleared')
  }
}

// Export singleton instance
export const newsService = new NewsService()
export default newsService
